import {
  defineComponent,
  computed,
  reactive,
  watch,
  ref,
  onMounted,
  onBeforeMount,
} from 'vue';
import styles from './index.module.less';
import copy from 'copy-to-clipboard';
import { ElMessage } from 'element-plus';
import clsx from 'clsx';
import { useFrameDataStore } from "@/store/frameDataStore";
import {
  getEnumName_LocStatus, getEnumName_LocMode, getEnumName_AnomalySeverity, getEnumName_RoadType,
  getEnumName_LocalizationSourceFrom, getEnumName_LocalizationRoadType,
  getEnumName_GpsInfo_GnssStatus, getEnumName_GpsInfo_GpsPositionStatus,
  get_SpeedLimitSource_name
} from "@/utils/enumHelperDataView";
import { ZCSToWGS84,GCJ02ToWGS84, WGS84ToGCJ02 } from '@/utils/coordinateHelper';

const weatherTypeMap = {
  0: '-',
  1: 'NORMAL',
  2: 'RAIN',
  3: 'FOG',
  4: 'SNOW',
  5: 'HEAVY_RAIN',
  6: 'LIGHT_SNOW',
  7: 'HEAVY_FOG',
};

function formatFreeMoveCommand(command: any) {
  if (!command) return '';
  const names = [];
  if (command.preview) names.push('preview');
  if (command.start) names.push('start');
  if (command.cancel) names.push('cancel');
  if (command.clear) names.push('clear');
  return names.join(', ');
}

const freeMoveVersionMap: Record<number, string> = {
  0: 'V0',
  3: 'V3',
};

const executionStateMap: Record<number, string> = {
  0: 'IDLE',
  1: 'REJECTED',
  2: 'IN_PROGRESS',
  3: 'SUCCEEDED',
  4: 'FAILED',
  5: 'PLANNING',
  6: 'READY_TO_GO',
  7: 'CANCELED',
};

const alarmFreeMoveStatusMap: Record<number, string> = {
  0: 'IDLE',
  1: 'PLANNING',
  2: 'READY_TO_EXECUTING',
  3: 'EXECUTING',
  4: 'CANCELED',
  5: 'SUCCEEDED',
  6: 'FAILED',
};

const freeMoveFailReasonMap: Record<number, string> = {
  0: 'UNSPECIFIED',
  1: 'INVALID_TARGET',
  2: 'NO_LOCALIZATION',
  3: 'VEHICLE_STATE_NOT_READY',
  4: 'ALREADY_AT_TARGET',
  5: 'TARGET_TOO_FAR',
  6: 'TARGET_MISMATCH_ROUTING',
  7: 'CANCELLED_BY_COMMAND',
  8: 'PLANNING_PATH_TIMEOUT',
  9: 'READY_TO_EXECUTE_TIMEOUT',
  10: 'EXECUTION_FAILED',
};

function formatFreeMoveVersion(planning: any) {
  const assistance = planning?.cloudAssistanceStatus;
  if (!assistance) return '';
  const version = assistance.freeMoveFunctionVersion;
  if (!version) return 'V0';
  return freeMoveVersionMap[version] || `V${version}`;
}

function formatFreeMoveCommandStatus(planning: any) {
  const version = planning?.cloudAssistanceStatus?.freeMoveFunctionVersion;
  if (version) {
    const commandStatus = planning.cloudAssistanceStatus?.freeMoveCommandStatus;
    const state = commandStatus?.state;
    if (state === undefined || state === null) return '';
    const stateText = executionStateMap[state] || String(state);
    const failReason = commandStatus?.failReason;
    if (!failReason) return stateText;
    const reasonText = freeMoveFailReasonMap[failReason] || String(failReason);
    return `${stateText}/${reasonText}`;
  }
  const statuses = (planning?.alarmLevelCodeList || [])
    .map((item: any) => item?.freeMoveStatus)
    .filter((status: any) => status !== undefined && status !== null);
  const active = statuses.filter((status: number) => status !== 0);
  const picked = active.length ? active : statuses;
  return [...new Set(picked.map((status: number) => alarmFreeMoveStatusMap[status] || String(status)))].join(', ');
}

const weatherModeMap = {
  0: '-',
  1: 'AUTO',
  2: 'OFF',
  3: 'RAIN',
  4: 'FOG',
  5: 'SNOW',
  6: 'HEAVY_RAIN',
  7: 'LIGHT_SNOW',
  8: 'NORMAL',
  9: 'HEAVY_FOG',
};

const Matrix = defineComponent({
  name: 'Matrix',
  setup() {

    onMounted(() => {

    });

    const matrixList = reactive({
      mapVersion: {
        label: "地图信息",
        text: "",
      },
      mapinfo: {
        label: "地图模式",
        text: "",
      },
      weather: {
        label: "天气",
        text: "",
      },
      roadType: {
        label: "道路类型",
        text: "",
      },
      vehicleOrigin: {
        label: "主车坐标",
        text: "",
      },
      laneIdList: { label: "laneIdList", text: "" },
      planningScenario: { label: "planScenario", text: "" },
      freeMoveFunctionVersion: { label: "挪车版本", text: "" },
      freeMoveCommand: { label: "挪车指令", text: "" },
      freeMoveCommandStatus: { label: "挪车状态", text: "" },
      velocity: {
        label: '底盘速度',
        text: ""
      },
      vehicleThrottle: {
        label: '油门',
        text: ""
      },
      vehicleBrake: {
        label: '刹车',
        text: ""
      },
      vanguardBrake: {
        label: '安全模块刹车',
        text: ""
      },
      speedLimitSource: {
        label: '限速',
        text: ""
      },
      type: {
        label: 'MapRoadType',
        text: ""
      },
      sourceFrom: {
        label: 'MapRoadSource',
        text: ""
      },
      gps_pos_status: {
        label: 'GpsPosStatus',
        text: ""
      },
      gnss_status: {
        label: 'GpsGnssStatus',
        text: ""
      },
      globalPosition: {
        label: 'gcj02',
        text: ""
      },
    });

    const onCopyClick = (content: string) => {
      if (copy(content)) {
        ElMessage.success('复制成功');
      }
    };

    //。。。。。。。。。。。。。
    let frameDataStore = useFrameDataStore();
    watch(() => frameDataStore.currentData, (e: any) => {
      try {
        if (e?.vehicleData) {
          let weatherType = weatherTypeMap[e?.vehicleData?.weatherType];
          let weatherCommand = weatherModeMap[e?.vehicleData?.weatherCommand];
          if (weatherType && weatherCommand) {
            matrixList.weather.text = `${weatherType}-${weatherCommand}`
          }

          matrixList.vehicleOrigin.text = `${e.vehicleData?.origin?.x.toFixed(4)},${e.vehicleData?.origin?.y.toFixed(4)}`
          if (e.vehicleData?.routing?.laneIdList?.length > 0) {
            matrixList.laneIdList.text = e.vehicleData?.routing?.laneIdList.slice(0, 2).join(';')
          }
          if (e.vehicleData?.planning?.planningScenario) {
            matrixList.planningScenario.text = e.vehicleData?.planning?.planningScenario
          }
          matrixList.freeMoveFunctionVersion.text = formatFreeMoveVersion(e.vehicleData?.planning);
          matrixList.freeMoveCommand.text = formatFreeMoveCommand(
            e.vehicleData?.businessResult?.remoteBusinessCommand?.remoteTakeoverCommand?.freeMoveCommand,
          );
          matrixList.freeMoveCommandStatus.text = formatFreeMoveCommandStatus(e.vehicleData?.planning);
          if (e.vehicleData?.vehicle) {
            matrixList.velocity.text = Number(e.vehicleData?.vehicle?.velocity).toFixed(2);
          }
          if (e.vehicleData?.scene) {
            matrixList.roadType.text = getEnumName_RoadType(e.vehicleData?.scene?.roadType);
          }
          matrixList.mapinfo.text = getEnumName_LocMode(e.vehicleData.locMode);
          matrixList.vehicleThrottle.text = (e.vehicleData.controlDebugData?.vehicleThrottle || 0).toFixed(2);
          matrixList.vehicleBrake.text = (e.vehicleData.controlDebugData?.vehicleBrake || 0).toFixed(2);
          matrixList.vanguardBrake.text = (e.vehicleData.controlDebugData?.vanguardBrake || 0).toFixed(2);
          matrixList.type.text = getEnumName_LocalizationRoadType(e.vehicleData.localization?.roadType)
          matrixList.sourceFrom.text = getEnumName_LocalizationSourceFrom(e.vehicleData.localization?.sourceFrom)
          matrixList.gps_pos_status.text = getEnumName_GpsInfo_GpsPositionStatus(e.vehicleData.gpsInfo?.gpsPosStatus)
          matrixList.gnss_status.text = getEnumName_GpsInfo_GnssStatus(e.vehicleData.gpsInfo?.gnssStatus)
          matrixList.speedLimitSource.text = `${get_SpeedLimitSource_name(e.vehicleData?.planning?.speedPlanningStatus?.speedLimitStatus.source)}-${e.vehicleData?.planning?.speedPlanningStatus?.speedLimitStatus.speedLimit}m/s`
          {
            let zcs_x = e.vehicleData?.localization?.globalPosition?.x;
            let zcs_y = e.vehicleData?.localization?.globalPosition?.y;
            let wgs=ZCSToWGS84(zcs_x, zcs_y)
            let gcj_lon=wgs?.lon
            let gcj_lat=wgs?.lat
            let gcj=WGS84ToGCJ02(gcj_lon, gcj_lat) 
            matrixList.globalPosition.text =`${gcj?.lon?.toFixed(5)},${gcj?.lat?.toFixed(5)}`
          }
        }
        if (e?.mapData) {
          matrixList.mapVersion.text = `${e?.mapData?.mapId}-${e?.mapData?.mapVersion}`
        }
      } catch (ex) {
        console.log(ex)
      }
    })
    return {
      matrixList,
      onCopyClick,
    };
  },
  render() {
    return (
      <div class={clsx('dark-bg', styles.container)}>
        {Object.keys(this.matrixList).map((key) => (
          // @ts-ignore
          <div
            key={key}
            onClick={() =>
              // @ts-ignore
              this.onCopyClick(`${this.matrixList[key].label}：${this.matrixList[key].text}`)
            }
            class={styles['matrix-item']}
          >
            <span class={styles['matrix-label']}>
              {/* @ts-ignore */}
              {this.matrixList[key].label}
            </span>
            <span class={styles['matrix-colon']}>:</span>
            <span class={styles['matrix-value']}>
              {/* @ts-ignore */}
              {this.matrixList[key].text}
            </span>
          </div>
        ))}
      </div>
    );
  },
});

export default Matrix;
